import { apiGet, apiPost, apiPatch, apiDeleteWithBody } from '@/utils/api';

// Types
export interface PcmAccount {
  id: string;
  compte: string;
  libelle: string;
  classe: number;
  category: PcmCategory;
  description?: string;
  defaultTvaRate: number;
  isActive: boolean;
  sortOrder: number;
}

export type ExpenseDocumentType =
  | 'INVOICE'
  | 'RECEIPT'
  | 'BON'
  | 'BON_COMMANDE'
  | 'BON_LIVRAISON'
  | 'CONTRAT'
  | 'DEVIS'
  | 'RELEVE_BANCAIRE'
  | 'OTHER';

export type ExpenseStatus = 'PENDING' | 'VALIDATED' | 'REJECTED' | 'PAID';

export type PcmCategory =
  | 'ACHATS_REVENDUS'
  | 'ACHATS_CONSOMMES'
  | 'CHARGES_EXTERNES'
  | 'IMPOTS_TAXES'
  | 'CHARGES_PERSONNEL'
  | 'AUTRES_CHARGES'
  | 'DOTATIONS'
  | 'CHARGES_FINANCIERES'
  | 'CHARGES_NON_COURANTES'
  | 'IMPOTS_RESULTATS';

// Embedded supplier info in expense response
export interface ExpenseSupplier {
  id: string;
  name: string;
  shortName?: string;
  ice?: string;
  ifiscal?: string;
  category?: string;
  address?: string;
  city?: string;
  phone?: string;
  email?: string;
}

export interface PlatformExpense {
  id: string;
  classe: number;
  compte: string;
  compteLibelle: string;
  description: string;
  reference?: string;
  supplierId?: string;
  supplier?: ExpenseSupplier;
  supplierName?: string;       // Legacy field
  supplierIce?: string;        // Legacy field
  supplierIf?: string;         // Legacy field
  amountHt: number;
  tvaRate: number;
  tvaAmount: number;
  amountTtc: number;
  tvaExempt: boolean;
  expenseDate: string;
  periodYear: number;
  periodMonth: number;
  documentType: ExpenseDocumentType;
  documentUrl?: string;
  status: ExpenseStatus;
  validatedBy?: string;
  validatedAt?: string;
  isPaid: boolean;
  paidAt?: string;
  paymentMethod?: string;
  paymentReference?: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateExpenseDto {
  compte: string;
  compteLibelle: string;
  description: string;
  reference?: string;
  supplierId?: string;         // Reference to supplier
  supplierName?: string;       // Legacy/manual entry
  supplierIce?: string;        // Legacy/manual entry
  supplierIf?: string;         // Legacy/manual entry
  amountHt: number;
  tvaRate?: number;
  tvaExempt?: boolean;
  expenseDate: string;
  documentType?: ExpenseDocumentType;
  documentUrl?: string;
  paymentMethod?: string;
  paymentReference?: string;
  isPaid?: boolean;
  paidAt?: string;
  notes?: string;
}

export interface UpdateExpenseDto extends Partial<CreateExpenseDto> {
  status?: ExpenseStatus;
}

export interface ExpenseFilters {
  periodYear?: number;
  periodMonth?: number;
  status?: ExpenseStatus;
  compte?: string;
  category?: PcmCategory;
  supplierName?: string;
  isPaid?: boolean;
}

export interface ExpenseStatistics {
  total: number;
  totalAmountHt: number;
  totalTva: number;
  totalAmountTtc: number;
  byStatus: {
    PENDING: number;
    VALIDATED: number;
    REJECTED: number;
    PAID: number;
  };
  validatedAmountHt: number;
  validatedTva: number;
  unpaidAmount: number;
}

// API Functions - Expenses
export const getExpenses = async (filters?: ExpenseFilters): Promise<PlatformExpense[]> => {
  const params = new URLSearchParams();
  if (filters?.periodYear) params.append('periodYear', filters.periodYear.toString());
  if (filters?.periodMonth) params.append('periodMonth', filters.periodMonth.toString());
  if (filters?.status) params.append('status', filters.status);
  if (filters?.compte) params.append('compte', filters.compte);
  if (filters?.category) params.append('category', filters.category);
  if (filters?.supplierName) params.append('supplierName', filters.supplierName);
  if (filters?.isPaid !== undefined) params.append('isPaid', filters.isPaid.toString());

  const query = params.toString() ? `?${params.toString()}` : '';
  return apiGet<PlatformExpense[]>(`/billing/expenses${query}`);
};

export const getExpense = async (id: string): Promise<PlatformExpense> => {
  return apiGet<PlatformExpense>(`/billing/expenses/${id}`);
};

export const createExpense = async (data: CreateExpenseDto): Promise<PlatformExpense> => {
  return apiPost<PlatformExpense>('/billing/expenses', data);
};

export const updateExpense = async (id: string, data: UpdateExpenseDto): Promise<PlatformExpense> => {
  return apiPatch<PlatformExpense>(`/billing/expenses/${id}`, data);
};

export const deleteExpense = async (id: string, motif: string): Promise<void> => {
  return apiDeleteWithBody<void>(`/billing/expenses/${id}`, { motif });
};

export const validateExpense = async (id: string): Promise<PlatformExpense> => {
  return apiPost<PlatformExpense>(`/billing/expenses/${id}/validate`, {});
};

export const rejectExpense = async (id: string, reason?: string): Promise<PlatformExpense> => {
  return apiPost<PlatformExpense>(`/billing/expenses/${id}/reject`, { reason });
};

export const getExpenseStatistics = async (year?: number, month?: number): Promise<ExpenseStatistics> => {
  const params = new URLSearchParams();
  if (year) params.append('year', year.toString());
  if (month) params.append('month', month.toString());
  const query = params.toString() ? `?${params.toString()}` : '';
  return apiGet<ExpenseStatistics>(`/billing/expenses/statistics${query}`);
};

// API Functions - PCM Accounts
export const getPcmAccounts = async (): Promise<PcmAccount[]> => {
  return apiGet<PcmAccount[]>('/billing/expenses/pcm-accounts');
};

export const seedPcmAccounts = async (): Promise<{ seeded: number }> => {
  return apiPost<{ seeded: number }>('/billing/expenses/pcm-accounts/seed', {});
};

// Upload expense document
export const uploadExpenseDocument = async (id: string, file: File): Promise<PlatformExpense> => {
  const token = localStorage.getItem('nexedu_access_token');
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/billing/expenses/${id}/upload`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Erreur lors de l\'upload');
  }

  return response.json();
};

// Download P&L PDF
export const downloadPlPdf = async (month: string): Promise<void> => {
  const token = localStorage.getItem('nexedu_access_token');
  const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}/api/billing/pnl/pdf?month=${month}`, {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    throw new Error('Erreur lors du téléchargement du PDF');
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `CPC-${month}.pdf`;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

// Helpers
export const formatCurrency = (amount: number | string | null | undefined): string => {
  if (amount == null) return '0,00 MAD';
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (isNaN(numAmount)) return '0,00 MAD';
  return new Intl.NumberFormat('fr-MA', {
    style: 'currency',
    currency: 'MAD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numAmount);
};

export const getStatusColor = (status: ExpenseStatus): string => {
  const colors: Record<ExpenseStatus, string> = {
    PENDING: 'bg-yellow-100 text-yellow-800',
    VALIDATED: 'bg-green-100 text-green-800',
    REJECTED: 'bg-red-100 text-red-800',
    PAID: 'bg-blue-100 text-blue-800',
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
};

export const getStatusLabel = (status: ExpenseStatus): string => {
  const labels: Record<ExpenseStatus, string> = {
    PENDING: 'En attente',
    VALIDATED: 'Validée',
    REJECTED: 'Rejetée',
    PAID: 'Payée',
  };
  return labels[status] || status;
};

export const getDocumentTypeLabel = (type: ExpenseDocumentType): string => {
  const labels: Record<ExpenseDocumentType, string> = {
    INVOICE: 'Facture',
    RECEIPT: 'Reçu',
    BON: 'Bon',
    BON_COMMANDE: 'Bon',
    BON_LIVRAISON: 'Bon',
    CONTRAT: 'Contrat',
    DEVIS: 'Devis',
    RELEVE_BANCAIRE: 'Relevé bancaire',
    OTHER: 'Autre',
  };
  return labels[type] || type;
};

export const getCategoryLabel = (category: PcmCategory): string => {
  const labels: Record<PcmCategory, string> = {
    ACHATS_REVENDUS: 'Achats revendus',
    ACHATS_CONSOMMES: 'Achats consommés',
    CHARGES_EXTERNES: 'Charges externes',
    IMPOTS_TAXES: 'Impôts et taxes',
    CHARGES_PERSONNEL: 'Charges de personnel',
    AUTRES_CHARGES: 'Autres charges',
    DOTATIONS: 'Dotations',
    CHARGES_FINANCIERES: 'Charges financières',
    CHARGES_NON_COURANTES: 'Charges non courantes',
    IMPOTS_RESULTATS: 'Impôts sur résultats',
  };
  return labels[category] || category;
};

// Get supplier display name from expense (prefers supplier relation over legacy fields)
export const getExpenseSupplierName = (expense: PlatformExpense): string => {
  if (expense.supplier) {
    return expense.supplier.shortName
      ? `${expense.supplier.shortName} (${expense.supplier.name})`
      : expense.supplier.name;
  }
  return expense.supplierName || 'Non spécifié';
};

// Get supplier ICE from expense
export const getExpenseSupplierIce = (expense: PlatformExpense): string | undefined => {
  return expense.supplier?.ice || expense.supplierIce;
};
